import { Component, EventEmitter, Input, Output, isDevMode, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ConfirmationDialogDetailSection {
  label?: string;
  items: string[];
  type?: 'warning' | 'info' | 'success';
}

/**
 * The button variants the confirm button supports.
 *
 * Only the *filled* variants of the global button system are allowed: the
 * confirm button does not carry the `.btn` base class (the dialog owns its own
 * sizing and typography), and the outlined variants — `btn-secondary`,
 * `btn-tertiary`, `btn-ghost`, `btn-muted`, `btn-*-secondary` — get their
 * border from `.btn` and would render borderless. A confirm button is the
 * dialog's primary action anyway, so a low-emphasis variant has no place here.
 *
 * Keeping this a union rather than `string` is the point: an unsupported value
 * is a compile error at the call site instead of a button that silently
 * renders in the wrong colour.
 */
export type ConfirmButtonVariant =
  | 'btn-primary'
  | 'btn-success'
  | 'btn-danger'
  | 'btn-warning';

/** Runtime mirror of {@link ConfirmButtonVariant}, for validation and tests. */
export const CONFIRM_BUTTON_VARIANTS: readonly ConfirmButtonVariant[] = [
  'btn-primary',
  'btn-success',
  'btn-danger',
  'btn-warning',
];

/**
 * Fallback when no variant is given. Destructive on purpose: every caller that
 * omits `confirmButtonClass` today is a delete/cancel/discard confirmation.
 */
export const DEFAULT_CONFIRM_BUTTON_VARIANT: ConfirmButtonVariant = 'btn-danger';

export interface ConfirmationDialogConfig {
  title: string;
  message: string;
  /** Optional structured details displayed as bullet points below the message */
  details?: ConfirmationDialogDetailSection[];
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: ConfirmButtonVariant;
  icon?: 'danger' | 'warning' | 'info';
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isVisible) {
      <div class="confirmation-overlay" (click)="onCancel()">
        <div class="confirmation-dialog" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <div class="dialog-icon" [ngClass]="{
              'icon-danger': config.icon === 'danger',
              'icon-warning': config.icon === 'warning',
              'icon-info': config.icon === 'info'
            }">
              <!-- Danger Icon -->
              @if (config.icon === 'danger') {
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              }
    
              <!-- Warning Icon -->
              @if (config.icon === 'warning') {
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              }
    
              <!-- Info Icon -->
              @if (config.icon === 'info') {
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              }
            </div>
    
            <button (click)="onCancel()" class="close-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
    
          <div class="dialog-content">
            <h3 class="dialog-title">{{ config.title }}</h3>
            <p class="dialog-message">{{ config.message }}</p>
    
            @if (config.details && config.details.length > 0) {
              <div class="dialog-details">
                @for (section of config.details; track $index) {
                  <div class="detail-section" [ngClass]="'detail-' + (section.type || 'info')">
                    @if (section.label) {
                      <div class="detail-label">{{ section.label }}</div>
                    }
                    <ul class="detail-items">
                      @for (item of section.items; track $index) {
                        <li>{{ item }}</li>
                      }
                    </ul>
                  </div>
                }
              </div>
            }
          </div>
    
          <div class="dialog-actions">
            <button
              (click)="onCancel()"
              class="cancel-btn">
              {{ config.cancelText || 'Abbrechen' }}
            </button>
            <button
              (click)="onConfirm()"
              class="confirm-btn"
              [ngClass]="confirmButtonVariant">
              {{ config.confirmText || 'Bestätigen' }}
            </button>
          </div>
        </div>
      </div>
    }
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './confirmation-dialog.component.css'
})
export class ConfirmationDialogComponent {
  @Input() isVisible = false;
  @Input() config: ConfirmationDialogConfig = {
    title: 'Bestätigung erforderlich',
    message: 'Sind Sie sicher?',
    icon: 'warning'
  };
  
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  /** Tracks values already reported, so change detection cannot spam the log. */
  private reportedUnknownVariants = new Set<string>();

  /**
   * The button class actually put on the confirm button.
   *
   * Anything outside {@link CONFIRM_BUTTON_VARIANTS} is refused rather than
   * passed through: an unstyled class would fall back to the dialog's own
   * default and look like a deliberate choice. TypeScript already rejects such
   * values at the call site; this guards the paths that reach the config
   * untyped (JSON, `as any`, dynamic construction).
   */
  get confirmButtonVariant(): ConfirmButtonVariant {
    const requested = this.config.confirmButtonClass;
    if (!requested) {
      return DEFAULT_CONFIRM_BUTTON_VARIANT;
    }
    if (CONFIRM_BUTTON_VARIANTS.includes(requested)) {
      return requested;
    }
    if (isDevMode() && !this.reportedUnknownVariants.has(requested)) {
      this.reportedUnknownVariants.add(requested);
      console.error(
        `[ConfirmationDialog] Unsupported confirmButtonClass "${requested}". ` +
          `Supported: ${CONFIRM_BUTTON_VARIANTS.join(', ')}. ` +
          `Falling back to "${DEFAULT_CONFIRM_BUTTON_VARIANT}".`
      );
    }
    return DEFAULT_CONFIRM_BUTTON_VARIANT;
  }

  onConfirm(): void {
    this.confirmed.emit();
    this.isVisible = false;
  }

  onCancel(): void {
    this.cancelled.emit();
    this.isVisible = false;
  }
}